import React, { useEffect, useState } from "react";
import DashboardLayout from "../components/DashboardLayout";
import { useLang } from "../contexts/LanguageContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Users, Store, Eye, Package, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";

const PLANS = ["trial", "starter", "pro", "premium"];

export default function AdminPanel() {
  const { t, lang } = useLang();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({ users: 0, restaurants: 0, items: 0, views: 0 });

  const load = async () => {
    const [u, s] = await Promise.all([api.get("/admin/users"), api.get("/admin/stats")]);
    setUsers(u.data);
    setStats(s.data);
  };
  useEffect(() => { load(); }, []);

  const changePlan = async (uid, plan) => {
    await api.put(`/admin/users/${uid}/plan`, { plan });
    load();
    toast.success("Updated");
  };

  const deleteUser = async (uid, email) => {
    const msg = lang === "de"
      ? `Soll der Nutzer "${email}" und alle seine Daten wirklich gelöscht werden?`
      : lang === "tr"
      ? `"${email}" kullanıcısı ve tüm verileri silinsin mi?`
      : `Delete user "${email}" and all their data?`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/admin/users/${uid}`);
      toast.success(lang === "de" ? "Gelöscht" : lang === "tr" ? "Silindi" : "Deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">{t("admin.title")}</div>
          <h1 className="text-3xl font-bold tracking-tight">qrmenu-pro.com Admin</h1>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: t("admin.totalUsers"), value: stats.users, icon: Users },
            { label: t("admin.totalRestaurants"), value: stats.restaurants, icon: Store },
            { label: t("dashboard.totalItems"), value: stats.items, icon: Package },
            { label: t("dashboard.totalViews"), value: stats.views, icon: Eye },
          ].map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="rounded-2xl border bg-card p-5">
                <Icon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mt-3">{s.label}</div>
                <div className="text-3xl font-bold mt-1">{s.value}</div>
              </div>
            );
          })}
        </div>
        <div className="rounded-2xl border bg-card overflow-hidden">
          <div className="p-5 border-b">
            <h3 className="font-bold">{t("admin.users")}</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/50">
                <tr>
                  <th className="text-left p-3 font-semibold">Name</th>
                  <th className="text-left p-3 font-semibold">Email</th>
                  <th className="text-left p-3 font-semibold">Role</th>
                  <th className="text-left p-3 font-semibold">Plan</th>
                  <th className="text-right p-3 font-semibold w-16"></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t" data-testid={`admin-user-${u.id}`}>
                    <td className="p-3 font-medium">{u.name}</td>
                    <td className="p-3 text-muted-foreground">{u.email}</td>
                    <td className="p-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${u.role === "admin" ? "bg-fuchsia-500/10 text-fuchsia-600" : "bg-secondary"}`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="p-3">
                      <select className="h-8 rounded-lg border bg-background px-2" value={u.plan} onChange={(e) => changePlan(u.id, e.target.value)} data-testid={`admin-plan-${u.id}`}>
                        {PLANS.map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </td>
                    <td className="p-3 text-right">
                      {u.role !== "admin" && !["admin@qrmenupro.com", "demo@qrmenupro.com"].includes((u.email || "").toLowerCase()) && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-rose-500 hover:text-rose-600 hover:bg-rose-500/10"
                          onClick={() => deleteUser(u.id, u.email)}
                          data-testid={`admin-delete-${u.id}`}
                          aria-label="Delete user"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                      {["admin@qrmenupro.com", "demo@qrmenupro.com"].includes((u.email || "").toLowerCase()) && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600 font-semibold" title="System account">
                          🔒
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
